import React, { useState } from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  background: #f3f4f6;
  padding: 2.5rem 1.5rem;
`;

const Title = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1.5rem;
  color: #480D05;
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  text-align: center;
  margin-bottom: 2rem;
  color: #221511;
`;

const FaqList = styled.div`
  border-radius: 0.375rem;
  padding: 1rem;
`;

const FaqItem = styled.div`
  margin-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 0.5rem;
`;

const FaqQuestion = styled.button<{ expanded: boolean }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  text-align: left;
  color: #480D05;
  background: none;
  border: none;
  font-size: 1.25rem;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  transition: color 0.2s;
  &:hover, &:focus {
    color: #221511;
  }
`;

const FaqAnswer = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  color: #221511;
  font-size: 1rem;
  transition: max-height 0.2s;
`;

const Chevron = styled.svg<{ expanded: boolean }>`
  width: 1.5rem;
  height: 1.5rem;
  transform: ${({ expanded }) => (expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
  color: #480D05;
`;

const MoreQuestions = styled.div`
  margin-top: 2rem;
  text-align: center;
`;

const MoreTitle = styled.h3`
  font-size: 1.875rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const MoreText = styled.p`
  font-size: 1.125rem;
  margin-bottom: 1rem;
`;

const ContactButton = styled.a`
  background: #F87B15;
  color: #fff;
  font-weight: 700;
  padding: 0.5rem 2rem;
  border-radius: 9999px;
  text-decoration: none;
  font-size: 1rem;
  transition: background 0.2s;
  display: inline-block;
  &:hover {
    background: #d65c00;
  }
`;

const faqs = [
  {
    q: "What's included in the Free Plan?",
    a: "The Free Plan gives you access to live video, motion alerts, and local storage support (microSD card included). Cloud storage and advanced AI features are available in paid plans.",
  },
  {
    q: "Can I change or cancel my plan anytime?",
    a: "Yes! You can upgrade, downgrade, or cancel your subscription at any time through the Sensforge app—no long-term commitments.",
  },
  {
    q: "How does cloud storage work?",
    a: "Cloud storage automatically saves your footage online, so you can access past recordings anytime. Paid plans offer 7-day or 30-day storage options.",
  },
  {
    q: "Which Sensforge plan is best for me?",
    a: `Free Plan – Great for basic monitoring with local storage.<br />Monthly Plan – Ideal for everyday security with AI detection and 7-day cloud storage.<br />Yearly Plan – Best for 24/7 recording, 30-day storage, and face recognition alerts.`,
  },
  {
    q: "What happens if I don't renew my plan?",
    a: "If your subscription ends, you'll lose access to cloud storage and AI features, but live view and local storage will still work with your microSD card.",
  },
  {
    q: "Can my Sensforge camera record continuously, or only when motion is detected?",
    a: "Yes! You have two recording options: 24/7 continuous recording or event-based recording. With continuous recording, your camera captures everything around the clock (requires your included microSD card or cloud storage plan depending on the plan you're on). If you prefer to save space, event-based recording automatically records only when motion is detected, allowing you to review and delete clips individually—so you keep only what matters. You can manage these settings in the Sensforge app.",
  },
];

export default function Questions() {
  const [expanded, setExpanded] = useState<number | null>(null);

  const toggleExpand = (index: number) => {
    setExpanded(expanded === index ? null : index);
  };

  return (
    <Wrapper>
      <Title>Frequently Asked Questions</Title>
      <Subtitle>
        If you're still not sure which plan is the right one for you, here are a few FAQs that may help.
      </Subtitle>
      <FaqList>
        {faqs.map((faq, idx) => (
          <FaqItem key={idx}>
            <FaqQuestion
              expanded={expanded === idx}
              onClick={() => toggleExpand(idx)}
              aria-expanded={expanded === idx}
              aria-controls={`faq-answer-${idx}`}
            >
              <span>{faq.q}</span>
              <Chevron
                expanded={expanded === idx}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7.41 7.03L12 11.61l4.59-4.58L18 10l-6 6-6-6z" />
              </Chevron>
            </FaqQuestion>
            {expanded === idx && (
              <FaqAnswer id={`faq-answer-${idx}`} dangerouslySetInnerHTML={{ __html: faq.a }} />
            )}
          </FaqItem>
        ))}
      </FaqList>
      <MoreQuestions>
        <MoreTitle>Have more questions?</MoreTitle>
        <MoreText>Our team is here to help. Reach out anytime for support.</MoreText>
        <ContactButton href="mailto:support@sensforge.com">
          Contact Us
        </ContactButton>
      </MoreQuestions>
    </Wrapper>
  );
}